// Speech-model choice for LIVE (streaming) transcription during a browser
// recording. Preview only: the saved transcript is re-transcribed in batch when
// the recording is finalized. Same rule as iOS and Android.
import { AUTO_DETECT } from "@/constants/transcriptionLanguages";

// US-only host: the default host is edge-routed and can process audio in the EU.
const AAI_STREAMING_URL = "wss://streaming.us.assemblyai.com/v3/ws";

// One-line flip. Universal-3.6 Pro streaming costs 3x multilingual, so it stays
// off until the cost is approved; everyone gets multilingual meanwhile.
export const LIVE_PRO_ENABLED = false;

export const LIVE_PRO_MODEL = "universal-3-6-pro";
export const LIVE_MULTILINGUAL_MODEL = "universal-streaming-multilingual";

// Languages Universal-3.6 Pro streaming accepts in `language_codes`.
export const LIVE_PRO_LANGUAGES = new Set([
  "af", "ar", "yue", "ca", "da", "nl", "en", "et", "fi", "fr", "gl", "de",
  "he", "hi", "it", "ja", "ko", "zh", "mr", "no", "nn", "fa", "pt", "ro",
  "ru", "es", "sv", "tr", "ur", "vi", "xh", "zu",
]); // prettier-ignore

// Chinese regions that may be Cantonese: never steer them to Mandarin.
const CANTONESE_AMBIGUOUS_REGIONS = new Set(["zh_hk", "zh_mo"]);

/**
 * The Universal-3.6 Pro `language_codes` entry for a recording language, or
 * null when Pro can't be pinned to it. Same rules, in the same order, as
 * Android (`aaiStreamingLanguage`) and iOS:
 *  1. normalise: trim, lowercase, "-" → "_";
 *  2. empty or "auto" → null;
 *  3. an exact Pro code wins ("yue", "en");
 *  4. "zh_hk" / "zh_mo" → null (possibly Cantonese);
 *  5. "base_region" → base, if the base is a Pro code ("en_us" → "en");
 *  6. anything else → null. Never defaults to English ("uk" is Ukrainian).
 */
export function liveProLanguage(language) {
  const code = String(language ?? "")
    .trim()
    .toLowerCase()
    .replaceAll("-", "_");
  if (!code || code === AUTO_DETECT) return null;
  if (CANTONESE_AMBIGUOUS_REGIONS.has(code)) return null;
  // Steps 3, 5 and 6 in one: no Pro code contains "_", so an exact code is
  // its own base, and a code whose base isn't a Pro code is null.
  const base = code.split("_")[0];
  return LIVE_PRO_LANGUAGES.has(base) ? base : null;
}

/**
 * THE single place the live speech model is chosen, for a recording language:
 * `{ speechModel, proLanguage }` — `proLanguage` is null on multilingual.
 */
export function liveStreamingConfig(
  language,
  { proEnabled = LIVE_PRO_ENABLED } = {},
) {
  const proLanguage = proEnabled ? liveProLanguage(language) : null;
  return proLanguage
    ? { speechModel: LIVE_PRO_MODEL, proLanguage }
    : { speechModel: LIVE_MULTILINGUAL_MODEL, proLanguage: null };
}

/**
 * Query params (besides the token) for a live session in `language`.
 * - Pro (enabled, and the language can be pinned): Pro steered to that
 *   language. Pro's turns already arrive formatted; `format_turns` is a
 *   Universal Streaming parameter, so it is not sent.
 * - Anything else, including auto-detect: multilingual with language detection
 *   and formatted turns.
 */
export function liveStreamingParams(language, options) {
  const { speechModel, proLanguage } = liveStreamingConfig(language, options);
  if (proLanguage) {
    return {
      sample_rate: "16000",
      speech_model: speechModel,
      language_codes: JSON.stringify([proLanguage]),
    };
  }
  return {
    sample_rate: "16000",
    speech_model: speechModel,
    language_detection: "true",
    format_turns: "true",
  };
}

/** The full WebSocket URL; every value (the token included) is query-encoded. */
export function liveStreamingUrl(token, language, options) {
  const params = new URLSearchParams({
    ...liveStreamingParams(language, options),
    token,
  });
  return `${AAI_STREAMING_URL}?${params.toString()}`;
}
